import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { updateSession } from '@repancy/core';
import { testIds } from '../../lib/testIds';

interface SystemPromptEditorProps {
  sessionId: string;
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 4,
  fontFamily: "var(--xp-font-body)",
  fontSize: 12,
  color: '#000',
};

const toggleBtnStyle: React.CSSProperties = {
  background: '#ece9d8',
  borderStyle: 'solid',
  borderWidth: 1,
  fontFamily: "var(--xp-font-body)",
  fontSize: 11,
  fontWeight: 'bold',
  padding: '1px 6px',
  cursor: 'pointer',
  color: '#000',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 120,
  resize: 'vertical',
  fontFamily: "var(--xp-font-mono)",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.5,
  padding: 6,
  background: '#fff',
  color: '#000',
  borderStyle: 'solid',
  borderWidth: 2,
  borderTopColor: '#808080',
  borderLeftColor: '#808080',
  borderBottomColor: '#dfdfdf',
  borderRightColor: '#dfdfdf',
  outline: 'none',
  boxSizing: 'border-box' as const,
};

function summarizeSystemPrompt(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length === 0) return 'empty';
  const lines = trimmed.split('\n').length;
  return `${lines} line${lines === 1 ? '' : 's'} · ${trimmed.length} chars`;
}

export function SystemPromptEditor({ sessionId }: SystemPromptEditorProps) {
  const dispatch = useAppDispatch();
  const systemPrompt = useAppSelector(
    (s) => s.promptSessions.sessions.entities[sessionId]?.systemPrompt ?? '',
  );
  // Collapsed by default so the user prompt is above the fold — most
  // sessions don't need to edit the system prompt every run.
  const [expanded, setExpanded] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(updateSession({ id: sessionId, changes: { systemPrompt: e.target.value } }));
    },
    [dispatch, sessionId],
  );

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={headerStyle}>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={`system-prompt-${sessionId}`}
          data-testid={testIds.promptLab.systemPromptToggle}
          style={toggleBtnStyle}
        >
          {expanded ? '▾' : '▸'} System Prompt
        </button>
        <span
          data-testid={testIds.promptLab.systemPromptSummary}
          style={{ color: '#374151', fontSize: 11 }}
        >
          {summarizeSystemPrompt(systemPrompt)}
        </span>
      </div>
      {expanded && (
        <textarea
          id={`system-prompt-${sessionId}`}
          style={textareaStyle}
          value={systemPrompt}
          onChange={handleChange}
          placeholder="You are a senior M&A attorney reviewing acquisition documents. Identify material risks, missing clauses, and non-standard terms. Flag anything that deviates from market-standard deal terms."
          data-testid={testIds.promptLab.systemPrompt}
        />
      )}
    </div>
  );
}
